import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Auth
import LoginPage from './pages/auth/LoginPage';
import SuperAdminLogin from './pages/superadmin/SuperAdminLogin';

// Admin Pages
import AdminLayout from './components/layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import CampaignManagement from './pages/admin/CampaignManagement';
import CampaignDetail from './pages/admin/CampaignDetail';
import MasterDataPage from './pages/admin/MasterDataPage';
import ScheduleManagement from './pages/admin/ScheduleManagement';
import AdminReports from './pages/admin/AdminReports';
import AdminInsights from './pages/admin/AdminInsights';
import CampaignPerformance from './pages/admin/CampaignPerformance';
import AuditLogs from './pages/admin/AuditLogs';
import AdminSettings from './pages/admin/AdminSettings';

// User Pages
import UserLayout from './components/layouts/UserLayout';
import UserDashboard from './pages/user/UserDashboard';
import MyCampaigns from './pages/user/MyCampaigns';
import UserCampaignDetail from './pages/user/UserCampaignDetail';
import UserBatchView from './pages/user/UserBatchView';
import UserProfile from './pages/user/UserProfile';
import UserActivity from './pages/user/UserActivity';
import UserHelp from './pages/user/UserHelp';

// Super Admin Pages
import SuperAdminLayout from './components/layouts/SuperAdminLayout';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import PortalManagement from './pages/superadmin/PortalManagement';
import CrossPortalUsers from './pages/superadmin/CrossPortalUsers';

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/user/dashboard'} />;
  }
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-spinner" style={{height:'100vh'}}><div className="spinner" /></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/user/dashboard'} /> : <LoginPage />} />
      <Route path="/superadmin/login" element={<SuperAdminLogin />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="campaigns" element={<CampaignManagement />} />
        <Route path="campaigns/:id" element={<CampaignDetail />} />
        <Route path="master-data" element={<MasterDataPage />} />
        <Route path="schedules" element={<ScheduleManagement />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="insights" element={<AdminInsights />} />
        <Route path="performance" element={<CampaignPerformance />} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      {/* User Routes */}
      <Route path="/user" element={<ProtectedRoute role="user"><UserLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard" element={<UserDashboard />} />
        <Route path="campaigns" element={<MyCampaigns />} />
        <Route path="campaigns/:id" element={<UserCampaignDetail />} />
        <Route path="campaigns/:id/batch/:batchId" element={<UserBatchView />} />
        <Route path="activity" element={<UserActivity />} />
        <Route path="help" element={<UserHelp />} />
        <Route path="profile" element={<UserProfile />} />
      </Route>

      {/* Super Admin Routes */}
      <Route path="/superadmin" element={<SuperAdminLayout />}>
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard" element={<SuperAdminDashboard />} />
        <Route path="portals" element={<PortalManagement />} />
        <Route path="users" element={<CrossPortalUsers />} />
      </Route>

      <Route path="/" element={<Navigate to="/login" />} />
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <ToastContainer position="top-right" autoClose={3000} theme="light" />
      </AuthProvider>
    </BrowserRouter>
  );
}
